import React, { useEffect, useMemo, useState } from 'react';
import { Search, Check, Loader2, X, ArrowRight, ArrowLeft, ChevronRight, Package, MapPin, Truck, FileText, Download, Phone, Mail, StickyNote, Clock, Plus, CalendarCheck, ChevronDown, AlertTriangle } from 'lucide-react';
import { RouteStep } from '../client/OrderRoute';
import { TrackingEvents, TrackingEvent } from '../client/TrackingEvents';
export type Status = 'Consegnata' | 'In transito' | 'Da firmare' | 'Fallita' | 'In ritardo' | 'In partenza' | 'In consegna' | 'Anomalia';
type Signed = 'ok' | 'pending' | 'failed' | 'none';
export const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
const IT_MONTHS: Record<string, number> = {
  gen: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  mag: 4,
  giu: 5,
  lug: 6,
  ago: 7,
  set: 8,
  ott: 9,
  nov: 10,
  dic: 11
};
function parseItalianDate(s: string): number {
  // Matches "09 mag 08:12" or "09 mag · 08:12"
  const m = s.match(/^(\d{1,2})\s+([a-z]{3})(?:[\s·]+)?(\d{1,2})?:?(\d{2})?/i);
  if (!m) return 0;
  const day = parseInt(m[1], 10);
  const monthKey = m[2].toLowerCase();
  const month = IT_MONTHS[monthKey] ?? 0;
  const hours = m[3] ? parseInt(m[3], 10) : 0;
  const minutes = m[4] ? parseInt(m[4], 10) : 0;
  // Assume current year context (sort is relative anyway)
  return new Date(2026, month, day, hours, minutes).getTime();
}
export type ShipmentApiData = {
  source: 'brt-live';
  currentPhase: string; // e.g. "Consegnata", "In consegna"
  currentPhaseRaw?: string; // raw API status if different from display
  lastStatusDate: string;
  lastStatusTime: string;
  delivered: boolean;
  isReturn: boolean;
  trackingRecovered?: string;
  giacenza?: {
    numero: string;
    apertura: string;
    motivazione?: string;
    dispoRequired: boolean;
  };
};
export type Shipment = {
  id: string;
  trackingId: string;
  store: string;
  address: string;
  city: string;
  carrier: string;
  colli: number;
  weight: string;
  status: Status;
  signed: Signed;
  cost: string;
  departure: string;
  eta: string;
  reference: string;
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  steps: RouteStep[];
  events: TrackingEvent[];
  apiData?: ShipmentApiData;
};
// --- Shipments store ------------------------------------------------------
// User-created shipments are prepended via `addShipment` and any subscribed
// component re-renders. Seed data (declared below) lives at the bottom of
// the array so newly added items appear first when sorted by departure.
const shipmentListeners = new Set<() => void>();
function subscribeShipments(fn: () => void): () => void {
  shipmentListeners.add(fn);
  return () => {
    shipmentListeners.delete(fn);
  };
}
function getShipmentsSnapshot(): Shipment[] {
  return SHIPMENTS;
}
export function addShipment(s: Shipment): void {
  SHIPMENTS = [s, ...SHIPMENTS];
  shipmentListeners.forEach(fn => fn());
}
function useShipments(): Shipment[] {
  return React.useSyncExternalStore(subscribeShipments, getShipmentsSnapshot, getShipmentsSnapshot);
}
let SHIPMENTS: Shipment[] = [{
  id: 'SH-7H2K9P',
  trackingId: 'FX4892013',
  store: 'Store Milano Duomo',
  address: 'Galleria Vittorio Emanuele II, 12',
  city: 'Milano',
  carrier: 'FedEx',
  colli: 6,
  weight: '48,2 kg',
  status: 'Da firmare',
  signed: 'pending',
  cost: '€ 48,20',
  departure: '09 mag 08:12',
  eta: '11 mag',
  reference: 'ORD-2840-119',
  contact: {
    name: 'Sara Bianchi',
    phone: '+39 02 8845 2210',
    email: 'milano.duomo@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '09 mag, 08:12',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Bologna',
    timestamp: '09 mag, 21:45',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Hub Milano',
    timestamp: '10 mag, 06:20',
    status: 'current'
  }, {
    label: 'In consegna',
    location: 'Milano centro',
    status: 'pending'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '10 mag',
    timestamp: '07:42',
    location: 'Hub Milano',
    description: 'Anomalia rilevata: orario di consegna fuori dalle aperture del negozio',
    type: 'warning'
  }, {
    date: '10 mag',
    timestamp: '06:20',
    location: 'Hub Milano',
    description: "Spedizione arrivata all'hub di destinazione",
    type: 'info'
  }, {
    date: '09 mag',
    timestamp: '21:45',
    location: 'Bologna',
    description: 'In transito hub Bologna',
    type: 'info'
  }, {
    date: '09 mag',
    timestamp: '08:12',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-3M8N1Q',
  trackingId: 'FX9923011',
  store: 'Store Roma Termini',
  address: 'Via del Corso 88',
  city: 'Roma',
  carrier: 'FedEx',
  colli: 4,
  weight: '32,4 kg',
  status: 'In transito',
  signed: 'pending',
  cost: '€ 62,10',
  departure: '08 mag 14:02',
  eta: 'Oggi 16:30',
  reference: 'ORD-2840-118',
  contact: {
    name: 'Luca Romano',
    phone: '+39 06 4815 9920',
    email: 'roma.termini@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '08 mag, 14:02',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Roma Smistamento',
    timestamp: '09 mag, 02:18',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Hub Roma',
    timestamp: '10 mag, 05:40',
    status: 'completed'
  }, {
    label: 'In consegna',
    location: 'Roma centro',
    timestamp: '10 mag, 12:10',
    status: 'current'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '10 mag',
    timestamp: '12:10',
    location: 'Roma',
    description: 'Spedizione affidata al corriere per la consegna',
    type: 'info'
  }, {
    date: '10 mag',
    timestamp: '05:40',
    location: 'Hub Roma',
    description: 'In transito hub Roma',
    type: 'info'
  }, {
    date: '08 mag',
    timestamp: '14:02',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-9F4D2R',
  trackingId: 'FX882211',
  store: 'Store Firenze Centro',
  address: 'Via Roma 24',
  city: 'Firenze',
  carrier: 'FedEx',
  colli: 5,
  weight: '41,0 kg',
  status: 'Da firmare',
  signed: 'pending',
  cost: '€ 41,65',
  departure: '09 mag 17:30',
  eta: '12 mag',
  reference: 'ORD-2840-117',
  contact: {
    name: 'Chiara Greco',
    phone: '+39 055 215 6630',
    email: 'firenze.centro@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '09 mag, 17:30',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Firenze',
    timestamp: '10 mag, 03:10',
    status: 'current'
  }, {
    label: 'In transito',
    location: 'Firenze',
    status: 'pending'
  }, {
    label: 'In consegna',
    location: 'Firenze centro',
    status: 'pending'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '10 mag',
    timestamp: '03:10',
    location: 'Hub Firenze',
    description: 'In smistamento presso hub Firenze',
    type: 'info'
  }, {
    date: '09 mag',
    timestamp: '17:30',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-5J7L8X',
  trackingId: 'UPS771203',
  store: 'Store Bari Mungivacca',
  address: 'Viale Luigi Einaudi 18',
  city: 'Bari',
  carrier: 'UPS',
  colli: 8,
  weight: '64,8 kg',
  status: 'In ritardo',
  signed: 'ok',
  cost: '€ 78,40',
  departure: '07 mag 09:15',
  eta: '11 mag',
  reference: 'ORD-2840-116',
  contact: {
    name: 'Marco Esposito',
    phone: '+39 080 5048 110',
    email: 'bari.mungivacca@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '07 mag, 09:15',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Napoli',
    timestamp: '08 mag, 02:40',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Hub Bari',
    timestamp: '09 mag, 06:20',
    status: 'current'
  }, {
    label: 'In consegna',
    location: 'Bari',
    status: 'pending'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '10 mag',
    timestamp: '09:00',
    location: 'Hub Bari',
    description: 'Ritardo: maltempo sulla tratta, riprogrammata per 11 mag',
    type: 'warning'
  }, {
    date: '09 mag',
    timestamp: '06:20',
    location: 'Hub Bari',
    description: 'In transito hub Bari',
    type: 'info'
  }, {
    date: '07 mag',
    timestamp: '09:15',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-2K6V4T',
  trackingId: 'FX5510328',
  store: 'Store Palermo La Torre',
  address: 'Via Vincenzo La Torre 7',
  city: 'Palermo',
  carrier: 'FedEx',
  colli: 7,
  weight: '55,3 kg',
  status: 'Consegnata',
  signed: 'ok',
  cost: '€ 64,95',
  departure: '05 mag 11:20',
  eta: '08 mag',
  reference: 'ORD-2840-110',
  contact: {
    name: 'Sofia Ferrari',
    phone: '+39 091 615 4422',
    email: 'palermo.latorre@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '05 mag, 11:20',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Napoli',
    timestamp: '06 mag, 03:10',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Palermo',
    timestamp: '07 mag, 18:40',
    status: 'completed'
  }, {
    label: 'In consegna',
    location: 'Palermo',
    timestamp: '08 mag, 09:50',
    status: 'completed'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    timestamp: '08 mag, 11:12',
    status: 'completed'
  }],
  events: [{
    date: '08 mag',
    timestamp: '11:12',
    location: 'Palermo',
    description: 'Consegna firmata da Sofia Ferrari',
    type: 'success'
  }, {
    date: '08 mag',
    timestamp: '09:50',
    location: 'Palermo',
    description: 'In consegna al negozio',
    type: 'info'
  }, {
    date: '05 mag',
    timestamp: '11:20',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-8P1S3W',
  trackingId: 'SDA445102',
  store: 'Store Catania Etnapolis',
  address: 'C.C. Etnapolis - Strada XV',
  city: 'Catania',
  carrier: 'SDA',
  colli: 3,
  weight: '24,1 kg',
  status: 'Fallita',
  signed: 'failed',
  cost: '€ 32,80',
  departure: '06 mag 10:00',
  eta: '09 mag',
  reference: 'ORD-2840-108',
  contact: {
    name: 'Andrea Conti',
    phone: '+39 095 712 8841',
    email: 'catania.etnapolis@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '06 mag, 10:00',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Napoli',
    timestamp: '07 mag, 04:20',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Catania',
    timestamp: '08 mag, 22:10',
    status: 'completed'
  }, {
    label: 'In consegna',
    location: 'Catania',
    timestamp: '09 mag, 10:30',
    status: 'completed'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '09 mag',
    timestamp: '11:15',
    location: 'Catania',
    description: 'Tentativo di consegna fallito: negozio chiuso',
    type: 'warning'
  }, {
    date: '09 mag',
    timestamp: '10:30',
    location: 'Catania',
    description: 'In consegna al negozio',
    type: 'info'
  }, {
    date: '06 mag',
    timestamp: '10:00',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-6B9C5Y',
  trackingId: 'FX998142',
  store: 'Store Torino Lingotto',
  address: 'Via Nizza 230',
  city: 'Torino',
  carrier: 'FedEx',
  colli: 4,
  weight: '36,2 kg',
  status: 'In transito',
  signed: 'none',
  cost: '€ 38,40',
  departure: '09 mag 16:00',
  eta: '12 mag',
  reference: 'ORD-2840-114',
  contact: {
    name: 'Elena Russo',
    phone: '+39 011 631 8855',
    email: 'torino.lingotto@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '09 mag, 16:00',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Milano',
    timestamp: '10 mag, 04:30',
    status: 'current'
  }, {
    label: 'In transito',
    location: 'Torino',
    status: 'pending'
  }, {
    label: 'In consegna',
    location: 'Torino centro',
    status: 'pending'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    status: 'pending'
  }],
  events: [{
    date: '10 mag',
    timestamp: '04:30',
    location: 'Hub Milano',
    description: 'In smistamento presso hub Milano',
    type: 'info'
  }, {
    date: '09 mag',
    timestamp: '16:00',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}, {
  id: 'SH-4N2Z7H',
  trackingId: 'FX2298471',
  store: 'Store Bologna Centro',
  address: "Via dell'Indipendenza 42",
  city: 'Bologna',
  carrier: 'FedEx',
  colli: 6,
  weight: '49,6 kg',
  status: 'Consegnata',
  signed: 'ok',
  cost: '€ 54,85',
  departure: '04 mag 09:40',
  eta: '06 mag',
  reference: 'ORD-2840-105',
  contact: {
    name: 'Francesco Marino',
    phone: '+39 051 248 9933',
    email: 'bologna.centro@network.store'
  },
  steps: [{
    label: 'Preso in carico',
    location: 'Magazzino Roma',
    timestamp: '04 mag, 09:40',
    status: 'completed'
  }, {
    label: 'Hub smistamento',
    location: 'Bologna',
    timestamp: '05 mag, 02:00',
    status: 'completed'
  }, {
    label: 'In transito',
    location: 'Bologna',
    timestamp: '05 mag, 14:30',
    status: 'completed'
  }, {
    label: 'In consegna',
    location: 'Bologna centro',
    timestamp: '06 mag, 09:15',
    status: 'completed'
  }, {
    label: 'Consegnato',
    location: 'Negozio',
    timestamp: '06 mag, 10:48',
    status: 'completed'
  }],
  events: [{
    date: '06 mag',
    timestamp: '10:48',
    location: 'Bologna',
    description: 'Consegna firmata da Francesco Marino',
    type: 'success'
  }, {
    date: '04 mag',
    timestamp: '09:40',
    location: 'Magazzino Roma',
    description: 'Spedizione partita dal magazzino centrale',
    type: 'pickup'
  }]
}];
const FILTERS = ['Tutte', 'In transito', 'Da firmare', 'Consegnate', 'Problemi'];
export function StatusBadge({
  status
}: {
  status: Status;
}) {
  const styles: Record<Status, string> = {
    Consegnata: 'bg-[#D4FF3A] text-[#0A0A0A]',
    'In transito': 'bg-[#1F1F1F] text-[#FAFAFA] border border-[#2A2A2A]',
    'Da firmare': 'bg-transparent text-[#D4FF3A] border border-[#D4FF3A]/40',
    Fallita: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
    'In ritardo': 'bg-[#F5A524]/10 text-[#F5A524] border border-[#F5A524]/20',
    'In partenza': 'bg-[#1F1F1F] text-[#9A9A9A] border border-[#2A2A2A]',
    'In consegna': 'bg-[#D4FF3A]/10 text-[#D4FF3A] border border-[#D4FF3A]/30',
    Anomalia: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${styles[status]}`} data-id="element-2702">
      {status}
    </span>;
}
function SignedIcon({
  s
}: {
  s: Signed;
}) {
  if (s === 'ok') return <Check className="h-4 w-4 text-[#D4FF3A]" data-id="element-2703" />;
  if (s === 'pending') return <Loader2 className="h-4 w-4 text-[#F5A524] animate-spin" data-id="element-2704" />;
  if (s === 'failed') return <X className="h-4 w-4 text-[#EF4444]" data-id="element-2705" />;
  return <span className="text-[#6B6B6B] text-xs" data-id="element-2706">—</span>;
}
function matchesFilter(s: Shipment, f: string) {
  if (f === 'Tutte') return true;
  if (f === 'In transito') return s.status === 'In transito' || s.status === 'In partenza';
  if (f === 'Da firmare') return s.status === 'Da firmare';
  if (f === 'Consegnate') return s.status === 'Consegnata';
  if (f === 'Problemi') return s.status === 'Fallita' || s.status === 'In ritardo';
  return true;
}
type SpedizioniProps = {
  selectedId?: string | null;
  onSelectedIdChange?: (id: string | null) => void;
  onNewShipment?: () => void;
};
export function Spedizioni({
  selectedId: controlledSelectedId,
  onSelectedIdChange,
  onNewShipment
}: SpedizioniProps = {}) {
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const selectedId = controlledSelectedId !== undefined ? controlledSelectedId : internalSelectedId;
  const setSelectedId = (id: string | null) => {
    if (onSelectedIdChange) onSelectedIdChange(id);else setInternalSelectedId(id);
  };
  const [filter, setFilter] = useState('Tutte');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const shipments = useShipments();
  // Reset to page 1 whenever filters change. Must be declared BEFORE any
  // conditional return to keep hook order stable across renders.
  useEffect(() => {
    setPage(1);
  }, [filter, query]);
  const selected = shipments.find(s => s.id === selectedId) || null;
  if (selected) {
    return <SpedizioneDetail shipment={selected} onBack={() => setSelectedId(null)} data-id="element-2707" />;
  }
  const filtered = shipments.filter(s => matchesFilter(s, filter) && (query.trim() === '' || s.id.toLowerCase().includes(query.toLowerCase()) || s.store.toLowerCase().includes(query.toLowerCase()) || s.city.toLowerCase().includes(query.toLowerCase()) || s.trackingId.toLowerCase().includes(query.toLowerCase())));
  // Sort by departure date descending (most recent first)
  const sorted = [...filtered].sort((a, b) => parseItalianDate(b.departure) - parseItalianDate(a.departure));
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paginated = sorted.slice(startIdx, startIdx + PAGE_SIZE);
  return <div className="space-y-6" data-id="element-2708">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-2709">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-2710">
          Spedizioni
        </h1>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-2711">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#181818] border border-[#262626] text-xs" data-id="element-2712">
            <Package className="h-3.5 w-3.5 text-[#D4FF3A]" data-id="element-2713" />
            <span className="text-[#FAFAFA] font-medium" data-id="element-2714">
              {SHIPMENTS.length} totali
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#181818] border border-[#262626] text-xs" data-id="element-2715">
            <Truck className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-2716" />
            <span className="text-[#9A9A9A]" data-id="element-2717">
              {shipments.filter(s => s.status === 'In transito' || s.status === 'In partenza').length}{' '}
              in viaggio
            </span>
          </div>
          {onNewShipment && <button type="button" onClick={onNewShipment} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-2718">
              <Plus className="h-4 w-4" data-id="element-2719" />
              Nuova spedizione
            </button>}
        </div>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl" data-id="element-2720">
        <div className="p-5 border-b border-[#262626] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-2721">
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto" data-id="element-2722">
            <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] w-max sm:w-auto" data-id="element-2723">
              {FILTERS.map(f => <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === f ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-2724">
                  {f}
                </button>)}
            </div>
          </div>

          <div className="relative" data-id="element-2725">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2726" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per ID, negozio, città..." className="w-full sm:w-64 pl-9 pr-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-2727" />
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-2728">
          {paginated.map(s => <button key={s.id} onClick={() => setSelectedId(s.id)} className="w-full text-left p-4 active:bg-[#0A0A0A] transition-colors" data-id="element-2729">
              <div className="flex items-start justify-between gap-3 mb-2" data-id="element-2730">
                <div className="min-w-0" data-id="element-2731">
                  <div className="font-mono text-xs text-[#FAFAFA]" data-id="element-2732">{s.id}</div>
                  <div className="font-mono text-[10px] text-[#6B6B6B] mt-0.5" data-id="element-2733">
                    {s.trackingId}
                  </div>
                  <div className="text-sm text-[#FAFAFA] mt-1 truncate" data-id="element-2734">
                    {s.store}
                  </div>
                </div>
                <StatusBadge status={s.status} data-id="element-2735" />
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-[#9A9A9A]" data-id="element-2736">
                <div className="flex items-center gap-1.5 min-w-0" data-id="element-2737">
                  <span data-id="element-2738">Roma</span>
                  <ArrowRight className="h-3 w-3 text-[#6B6B6B] shrink-0" data-id="element-2739" />
                  <span className="truncate" data-id="element-2740">{s.city}</span>
                  <span className="text-[#6B6B6B] mx-1" data-id="element-2741">•</span>
                  <span className="font-mono whitespace-nowrap" data-id="element-2742">
                    {s.colli} colli
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-2743">
                  <SignedIcon s={s.signed} data-id="element-2744" />
                  <span className="font-mono text-[#FAFAFA]" data-id="element-2745">{s.cost}</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#6B6B6B] font-mono" data-id="element-2746">
                <span data-id="element-2747">{s.carrier}</span>
                <span data-id="element-2748">ETA {s.eta}</span>
              </div>
            </button>)}
          {filtered.length === 0 && <div className="p-8 text-center text-sm text-[#6B6B6B]" data-id="element-2749">
              Nessuna spedizione corrisponde ai filtri.
            </div>}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-2750">
          <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-2751">
            <thead data-id="element-2752">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-2753">
                <th className="px-5 py-3 font-medium" data-id="element-2754">Spedizione</th>
                <th className="px-5 py-3 font-medium" data-id="element-2755">Negozio</th>
                <th className="px-5 py-3 font-medium" data-id="element-2756">Vettore</th>
                <th className="px-5 py-3 font-medium" data-id="element-2757">Destinazione</th>
                <th className="px-5 py-3 font-medium text-center" data-id="element-2758">Colli</th>
                <th className="px-5 py-3 font-medium" data-id="element-2759">Stato</th>
                <th className="px-5 py-3 font-medium text-center" data-id="element-2760">Firma</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-2761">Costo</th>
                <th className="px-5 py-3 font-medium" data-id="element-2762">Partenza</th>
                <th className="px-5 py-3 font-medium" data-id="element-2763">Arrivo</th>
                <th className="px-5 py-3 font-medium w-8" data-id="element-2764" />
              </tr>
            </thead>
            <tbody data-id="element-2765">
              {filtered.map(s => <tr key={s.id} onClick={() => setSelectedId(s.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-2766">
                  <td className="px-5 py-3.5" data-id="element-2767">
                    <div className="font-mono text-xs text-[#FAFAFA]" data-id="element-2768">
                      {s.id}
                    </div>
                    <div className="font-mono text-[10px] text-[#6B6B6B] mt-0.5" data-id="element-2769">
                      {s.trackingId}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[#FAFAFA]" data-id="element-2770">{s.store}</td>
                  <td className="px-5 py-3.5" data-id="element-2771">
                    <div className="flex items-center gap-2" data-id="element-2772">
                      <div className="w-7 h-7 rounded bg-white flex items-center justify-center shrink-0 p-1" data-id="element-2773">
                        <img src={CARRIER_LOGOS[s.carrier]} alt={s.carrier} className="max-w-full max-h-full object-contain" data-id="element-2774" />
                      </div>
                      <span className="text-xs text-[#9A9A9A]" data-id="element-2775">
                        {s.carrier}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[#9A9A9A]" data-id="element-2776">
                    <span className="inline-flex items-center gap-1.5" data-id="element-2777">
                      <span data-id="element-2778">Roma</span>
                      <ArrowRight className="h-3 w-3 text-[#6B6B6B]" data-id="element-2779" />
                      <span data-id="element-2780">{s.city}</span>
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center font-mono text-[#FAFAFA]" data-id="element-2781">
                    {s.colli}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-2782">
                    <StatusBadge status={s.status} data-id="element-2783" />
                  </td>
                  <td className="px-5 py-3.5" data-id="element-2784">
                    <div className="flex justify-center" data-id="element-2785">
                      <SignedIcon s={s.signed} data-id="element-2786" />
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono text-[#FAFAFA]" data-id="element-2787">
                    {s.cost}
                  </td>
                  <td className="px-5 py-3.5 text-[#9A9A9A] font-mono text-xs" data-id="element-2788">
                    {s.departure}
                  </td>
                  <td className="px-5 py-3.5 text-[#FAFAFA] font-mono text-xs" data-id="element-2789">
                    {s.eta}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-2790">
                    <ChevronRight className="h-4 w-4 text-[#6B6B6B] inline" data-id="element-2791" />
                  </td>
                </tr>)}
              {filtered.length === 0 && <tr data-id="element-2792">
                  <td colSpan={11} className="px-5 py-10 text-center text-sm text-[#6B6B6B]" data-id="element-2793">
                    Nessuna spedizione corrisponde ai filtri.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
const ETA_SLOTS = ['09:00–13:00', '14:00–18:00', '08:00–12:00', '15:00–19:00'] as const;
function getEtaSlot(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = hash * 31 + id.charCodeAt(i) | 0;
  return ETA_SLOTS[Math.abs(hash) % ETA_SLOTS.length];
}
export function SpedizioneDetail({
  shipment,
  onBack,
  banner,
  extra,
  infoGrid,
  backLabel = 'Torna alle spedizioni'
}: {
  shipment: Shipment;
  onBack: () => void;
  banner?: React.ReactNode;
  extra?: React.ReactNode;
  infoGrid?: React.ReactNode | null;
  backLabel?: string;
}) {
  const [phasesExpanded, setPhasesExpanded] = useState(false);
  const currentIdx = shipment.steps.findIndex(s => s.status === 'current');
  const completedCount = shipment.steps.filter(s => s.status === 'completed').length;
  const currentStep = currentIdx >= 0 ? shipment.steps[currentIdx] : shipment.steps[Math.max(0, completedCount - 1)];
  // Exception detection: failed delivery attempt, in giacenza, or
  // returned to sender → render the progress bar + ETA in error state.
  const rawPhase = (shipment.apiData?.currentPhaseRaw || '').toUpperCase();
  const failedDelivery = rawPhase.includes('MANCATA') || rawPhase.includes('TENTATIVO') || rawPhase.includes('FALLIT');
  const isException = shipment.status === 'Fallita' || shipment.status === 'Anomalia' || !!shipment.apiData?.giacenza || shipment.apiData?.isReturn === true || failedDelivery;
  const exceptionLabel = (() => {
    if (shipment.apiData?.isReturn) return 'Reso al mittente';
    if (shipment.apiData?.giacenza) return 'In giacenza';
    if (failedDelivery || shipment.status === 'Fallita') return 'Tentativo di consegna fallito';
    if (shipment.status === 'Anomalia') return 'Anomalia in corso';
    return 'Spedizione bloccata';
  })();
  return <div className="space-y-6" data-id="element-2794">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-2795">
        <ArrowLeft className="h-4 w-4" data-id="element-2796" />
        <span data-id="element-2797">{backLabel}</span>
      </button>

      {banner}

      {/* Header */}
      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-2798">
        {/* Top row: identity + KPIs */}
        <div className="p-4 sm:p-5 flex items-start justify-between gap-6 flex-wrap" data-id="element-2799">
          <div className="flex items-center gap-4 min-w-0" data-id="element-2800">
            <div className="h-14 w-16 rounded-lg bg-white flex items-center justify-center shrink-0 p-2" data-id="element-2801">
              <img src={CARRIER_LOGOS[shipment.carrier]} alt={shipment.carrier} className="max-w-full max-h-full object-contain" data-id="element-2802" />
            </div>
            <div className="min-w-0" data-id="element-2803">
              <div className="flex items-center gap-2 mb-1" data-id="element-2804">
                <Package className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-2805" />
                <span className="text-xs font-mono text-[#9A9A9A] truncate" data-id="element-2806">
                  Tracking {shipment.trackingId}
                </span>
              </div>
              <div className="text-xl font-semibold text-[#FAFAFA] font-heading font-mono" data-id="element-2807">
                {shipment.id}
              </div>
              <div className="text-sm text-[#9A9A9A] mt-0.5 flex items-center gap-1.5" data-id="element-2808">
                <MapPin className="h-3.5 w-3.5 shrink-0" data-id="element-2809" />
                <span className="truncate" data-id="element-2810">
                  {shipment.store} — {shipment.address}, {shipment.city}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-stretch gap-5 sm:gap-8 shrink-0" data-id="element-2811">
            <div data-id="element-2812">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2813">
                Stato
              </div>
              <div className="mt-1.5" data-id="element-2814">
                <StatusBadge status={shipment.status} data-id="element-2815" />
              </div>
            </div>
            <div className="border-l border-[#262626] pl-5 sm:pl-8" data-id="element-2816">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2817">
                Costo
              </div>
              <div className="text-sm font-semibold text-[#FAFAFA] font-mono mt-1.5" data-id="element-2818">
                {shipment.cost}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row: full-width timeline progress + phases */}
        <div className="border-t border-[#262626] bg-[#0F0F0F]" data-id="element-2819">
          {/* Top dates + progress bar */}
          <div className="px-4 sm:px-5 pt-4 pb-3" data-id="element-2820">
            <div className="flex items-end justify-between mb-2.5" data-id="element-2821">
              <div data-id="element-2822">
                <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-0.5" data-id="element-2823">
                  Spedito
                </div>
                <div className="font-mono text-sm text-[#9A9A9A]" data-id="element-2824">
                  {shipment.departure}
                </div>
              </div>
              <div className="text-right" data-id="element-2825">
                {isException ? <>
                    <div className="text-[10px] uppercase tracking-wider text-[#EF4444] font-semibold mb-0.5 flex items-center justify-end gap-1" data-id="element-2826">
                      <AlertTriangle className="h-3.5 w-3.5 text-[#EF4444]" data-id="element-2827" />
                      {exceptionLabel}
                    </div>
                    <div className="font-mono text-base sm:text-lg font-bold text-[#EF4444]" data-id="element-2828">
                      Consegna a rischio
                    </div>
                    <div className="text-[10px] font-mono text-[#9A9A9A] mt-0.5" data-id="element-2829">
                      ETA prevista: {shipment.eta} · {getEtaSlot(shipment.id)}
                    </div>
                  </> : <>
                    <div className="text-[10px] uppercase tracking-wider text-[#D4FF3A]/80 font-semibold mb-0.5 flex items-center justify-end gap-1" data-id="element-2830">
                      <CalendarCheck className="h-3.5 w-3.5 text-[#D4FF3A]" data-id="element-2831" />
                      Consegna prevista
                    </div>
                    <div className="font-mono text-base sm:text-lg font-bold text-[#FAFAFA]" data-id="element-2832">
                      {shipment.eta}
                    </div>
                    <div className="text-[10px] font-mono text-[#9A9A9A] mt-0.5" data-id="element-2833">
                      Fascia {getEtaSlot(shipment.id)}
                    </div>
                  </>}
              </div>
            </div>

            <div className="relative h-2 w-full bg-[#0A0A0A] border border-[#262626] rounded-full mb-2" data-id="element-2834">
              <div className={`absolute top-0 left-0 h-full rounded-full ${isException ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} style={{
              width: `${Math.max(4, Math.min(96, completedCount / shipment.steps.length * 100))}%`
            }} data-id="element-2835" />
              <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full ring-2 ring-[#0F0F0F] ${isException ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} style={{
              left: `${Math.max(4, Math.min(96, completedCount / shipment.steps.length * 100))}%`
            }} data-id="element-2836" />
            </div>

            <div className={`text-[11px] text-center ${isException ? 'text-[#EF4444]' : 'text-[#6B6B6B]'}`} data-id="element-2837">
              {isException ? <span className="inline-flex items-center gap-1" data-id="element-2838">
                  <AlertTriangle className="h-3 w-3" data-id="element-2839" />
                  {exceptionLabel} — flusso interrotto
                </span> : <>
                  {completedCount} di {shipment.steps.length} fasi completate
                </>}
            </div>
          </div>

          {/* Current phase + expand button */}
          <button type="button" onClick={() => setPhasesExpanded(v => !v)} aria-expanded={phasesExpanded} className="w-full border-t border-[#262626] px-4 sm:px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#141414] transition text-left" data-id="element-2840">
            <div className="flex items-center gap-3 min-w-0" data-id="element-2841">
              <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-2842">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${isException ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} data-id="element-2843" />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isException ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} data-id="element-2844" />
              </span>
              <div className="min-w-0" data-id="element-2845">
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${isException ? 'text-[#EF4444]/90' : 'text-[#D4FF3A]/80'}`} data-id="element-2846">
                  {isException ? exceptionLabel : 'Fase attuale'}
                </div>
                <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-2847">
                  {currentStep?.label ?? '—'}
                  <span className="text-[#9A9A9A] font-normal" data-id="element-2848">
                    {currentStep?.location && ` · ${currentStep.location}`}
                    {currentStep?.timestamp && ` · ${currentStep.timestamp}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#9A9A9A] shrink-0" data-id="element-2849">
              <span className="hidden sm:inline" data-id="element-2850">
                {phasesExpanded ? 'Nascondi fasi' : 'Vedi tutte le fasi'}
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${phasesExpanded ? 'rotate-180' : ''}`} data-id="element-2851" />
            </div>
          </button>

          {/* Expandable full phase list */}
          {phasesExpanded && <div className="border-t border-[#262626] px-4 sm:px-5 py-4" data-id="element-2852">
              <ol className="relative space-y-3" data-id="element-2853">
                {shipment.steps.map((s, i) => {
              const isCompleted = s.status === 'completed';
              const isCurrent = s.status === 'current';
              const isLast = i === shipment.steps.length - 1;
              return <li key={`${s.label}-${i}`} className="relative flex items-start gap-3" data-id="element-2854">
                      {/* connector line */}
                      {!isLast && <span className={`absolute left-[7px] top-4 bottom-[-12px] w-px ${isCompleted ? 'bg-[#D4FF3A]/40' : 'bg-[#262626]'}`} aria-hidden data-id="element-2855" />}
                      {/* dot */}
                      <span className={`relative z-10 mt-1 h-3.5 w-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${isCompleted ? 'bg-[#D4FF3A] border-[#D4FF3A]' : isCurrent ? 'bg-[#0F0F0F] border-[#D4FF3A]' : 'bg-[#0F0F0F] border-[#333]'}`} data-id="element-2856">
                        {isCompleted && <Check className="h-2 w-2 text-[#0A0A0A]" data-id="element-2857" />}
                        {isCurrent && <span className="h-1.5 w-1.5 rounded-full bg-[#D4FF3A]" data-id="element-2858" />}
                      </span>
                      <div className="min-w-0 flex-1 pb-0.5" data-id="element-2859">
                        <div className="flex items-center justify-between gap-3" data-id="element-2860">
                          <div className={`text-sm font-medium ${isCompleted || isCurrent ? 'text-[#FAFAFA]' : 'text-[#6B6B6B]'}`} data-id="element-2861">
                            {s.label}
                          </div>
                          {s.timestamp && <div className="text-[11px] font-mono text-[#9A9A9A] shrink-0" data-id="element-2862">
                              {s.timestamp}
                            </div>}
                        </div>
                        <div className="text-xs text-[#9A9A9A] mt-0.5 flex items-center gap-1" data-id="element-2863">
                          <MapPin className="h-3 w-3 text-[#6B6B6B]" data-id="element-2864" />
                          {s.location}
                          {isCurrent && <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#D4FF3A]" data-id="element-2865">
                              · In corso
                            </span>}
                        </div>
                      </div>
                    </li>;
            })}
              </ol>
            </div>}
        </div>
      </div>

      {/* Info grid — overridable via infoGrid prop */}
      {infoGrid !== undefined ? infoGrid : <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2866">
          <InfoCard title="Dettagli spedizione" headerRight={<span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium font-mono" data-id="element-2868">
                {shipment.id}
              </span>} footer={<button className="w-full inline-flex items-center justify-end gap-1 text-xs font-medium text-[#9A9A9A] hover:text-[#D4FF3A] transition" data-id="element-2869">
                Vedi storico modifiche
                <ArrowRight className="h-3.5 w-3.5" data-id="element-2870" />
              </button>} data-id="element-2867">
            <InfoRow label="Riferimento ordine" value={shipment.reference} mono data-id="element-2871" />
            <InfoRow label="Colli" value={`${shipment.colli}`} mono data-id="element-2872" />
            <InfoRow label="Peso" value={shipment.weight} mono data-id="element-2873" />
            <InfoRow label="Vettore" value={shipment.carrier} data-id="element-2874" />
            <InfoRow label="Partenza" value={`Magazzino Roma — ${shipment.departure}`} data-id="element-2875" />
          </InfoCard>

          <InfoCard title="Negozio" headerRight={<span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium truncate max-w-[120px]" data-id="element-2877">
                {shipment.city}
              </span>} footer={<div className="flex items-center justify-between gap-2" data-id="element-2878">
                <a href={`tel:${shipment.contact.phone}`} className="inline-flex items-center gap-1 text-xs font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-2879">
                  <Phone className="h-3.5 w-3.5" data-id="element-2880" />
                  Chiama
                </a>
                <button className="inline-flex items-center gap-1 text-xs font-medium text-[#9A9A9A] hover:text-[#D4FF3A] transition" data-id="element-2881">
                  Apri scheda negozio
                  <ArrowRight className="h-3.5 w-3.5" data-id="element-2882" />
                </button>
              </div>} data-id="element-2876">
            <InfoRow label="Punto vendita" value={shipment.store} data-id="element-2883" />
            <InfoRow label="Indirizzo" value={`${shipment.address}, ${shipment.city}`} data-id="element-2884" />
            <InfoRow label="Referente" value={shipment.contact.name} data-id="element-2885" />
            <InfoRow label="Telefono" value={shipment.contact.phone} icon={<Phone className="h-3 w-3 text-[#6B6B6B]" data-id="element-2887" />} data-id="element-2886" />
            <InfoRow label="Email" value={shipment.contact.email} icon={<Mail className="h-3 w-3 text-[#6B6B6B]" data-id="element-2889" />} data-id="element-2888" />
          </InfoCard>

          <ContenutoCard shipment={shipment} data-id="element-2890" />
        </div>}

      {extra}
    </div>;
}
function InfoCard({
  title,
  badge,
  headerRight,
  footer,
  children
}: {
  title: string;
  badge?: React.ReactNode;
  headerRight?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl flex flex-col h-full" data-id="element-2891">
      {/* HEADER */}
      <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between gap-3" data-id="element-2892">
        <div className="flex items-center gap-2 min-w-0" data-id="element-2893">
          <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-2894">
            {title}
          </h3>
          {badge}
        </div>
        {headerRight && <div className="flex items-center gap-2 shrink-0" data-id="element-2895">{headerRight}</div>}
      </div>
      {/* BODY */}
      <div className="flex-1 p-5" data-id="element-2896">
        <div className="space-y-2.5" data-id="element-2897">{children}</div>
      </div>
      {/* FOOTER */}
      {footer && <div className="border-t border-[#262626] px-5 py-3" data-id="element-2898">{footer}</div>}
    </div>;
}
function InfoRow({
  label,
  value,
  mono,
  icon
}: {
  label: string;
  value: string;
  mono?: boolean;
  icon?: React.ReactNode;
}) {
  return <div data-id="element-2899">
      <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2900">
        {label}
      </div>
      <div className={`text-sm text-[#FAFAFA] mt-0.5 flex items-center gap-1.5 ${mono ? 'font-mono' : ''}`} data-id="element-2901">
        {icon}
        <span className="truncate" data-id="element-2902">{value}</span>
      </div>
    </div>;
}
function DocRow({
  label,
  filename,
  disabled
}: {
  label: string;
  filename: string;
  disabled?: boolean;
}) {
  return <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]" data-id="element-2903">
      <div className="flex items-center gap-2.5 min-w-0" data-id="element-2904">
        <div className="h-7 w-7 rounded bg-[#181818] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-2905">
          <FileText className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-2906" />
        </div>
        <div className="min-w-0" data-id="element-2907">
          <div className="text-xs font-medium text-[#FAFAFA]" data-id="element-2908">{label}</div>
          <div className="text-[10px] font-mono text-[#6B6B6B] truncate" data-id="element-2909">
            {disabled ? 'In attesa di firma' : filename}
          </div>
        </div>
      </div>
      <button disabled={disabled} className={`h-7 w-7 rounded flex items-center justify-center shrink-0 transition-colors ${disabled ? 'text-[#6B6B6B] cursor-not-allowed' : 'text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818]'}`} aria-label={`Scarica ${label}`} data-id="element-2910">
        <Download className="h-3.5 w-3.5" data-id="element-2911" />
      </button>
    </div>;
}
// --- Contenuto card + modal --------------------------------------------
// Shows the list of colli (packages) with item count. Clicking a collo
// opens a modal listing every article inside that package.
type ColloItem = {
  sku: string;
  name: string;
  qty: number;
  size?: string;
};
type Collo = {
  id: string;
  label: string;
  weight: string;
  dimensions: string;
  items: ColloItem[];
};
function buildColliForShipment(shipment: Shipment): Collo[] {
  // Deterministic mock content per shipment so the data is stable across
  // re-renders but varies per shipment.
  const seed = shipment.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const sampleProducts = [{
    sku: 'TS-NER-M',
    name: 'T-shirt nera basic',
    size: 'M'
  }, {
    sku: 'TS-BIA-L',
    name: 'T-shirt bianca basic',
    size: 'L'
  }, {
    sku: 'JN-DEN-32',
    name: 'Jeans denim slim',
    size: '32'
  }, {
    sku: 'SW-GRI-M',
    name: 'Felpa grigia con cappuccio',
    size: 'M'
  }, {
    sku: 'SK-NER-42',
    name: 'Sneakers nere',
    size: '42'
  }, {
    sku: 'GI-BLU-S',
    name: 'Giacca leggera blu',
    size: 'S'
  }, {
    sku: 'AC-CIN-U',
    name: 'Cintura in pelle',
    size: 'U'
  }, {
    sku: 'BR-CAR-U',
    name: 'Berretto cargo',
    size: 'U'
  }];
  const count = Math.max(1, shipment.colli);
  return Array.from({
    length: count
  }).map((_, idx) => {
    const itemsCount = (seed + idx * 3) % 4 + 1;
    const items: ColloItem[] = Array.from({
      length: itemsCount
    }).map((__, j) => {
      const p = sampleProducts[(seed + idx * 5 + j * 7) % sampleProducts.length];
      const qty = (seed + j) % 3 + 1;
      return {
        sku: p.sku,
        name: p.name,
        qty,
        size: p.size
      };
    });
    return {
      id: `${shipment.id}-C${idx + 1}`,
      label: `Collo ${idx + 1} di ${count}`,
      weight: shipment.weight && shipment.weight !== '—' ? shipment.weight : '—',
      dimensions: idx % 2 === 0 ? '40 × 30 × 20 cm' : '50 × 35 × 25 cm',
      items
    };
  });
}
function ContenutoCard({
  shipment
}: {
  shipment: Shipment;
}) {
  const colli = useMemo(() => buildColliForShipment(shipment), [shipment]);
  const [openColloId, setOpenColloId] = useState<string | null>(null);
  const openCollo = colli.find(c => c.id === openColloId) || null;
  const totalItems = colli.reduce((acc, c) => acc + c.items.reduce((s, i) => s + i.qty, 0), 0);
  return <>
      <InfoCard title="Contenuto" headerRight={<span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium font-mono" data-id="element-2913">
            {totalItems} articoli · {colli.length} colli
          </span>} footer={<button className="w-full inline-flex items-center justify-end gap-1 text-xs font-medium text-[#9A9A9A] hover:text-[#D4FF3A] transition" data-id="element-2914">
            <Download className="h-3.5 w-3.5" data-id="element-2915" />
            Scarica DDT completo
          </button>} data-id="element-2912">
        <div className="space-y-2" data-id="element-2916">
          {colli.map(c => <button key={c.id} onClick={() => setOpenColloId(c.id)} className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] hover:border-[#333] transition-colors text-left" data-id="element-2917">
              <div className="flex items-center gap-3 min-w-0" data-id="element-2918">
                <div className="h-8 w-8 rounded-lg bg-[#181818] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-2919">
                  <Package className="h-4 w-4 text-[#D4FF3A]" data-id="element-2920" />
                </div>
                <div className="min-w-0" data-id="element-2921">
                  <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-2922">
                    {c.label}
                  </div>
                  <div className="text-[10px] font-mono text-[#6B6B6B] truncate" data-id="element-2923">
                    {c.items.length} articoli · {c.dimensions}
                    {c.weight !== '—' && <> · {c.weight}</>}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#6B6B6B] shrink-0" data-id="element-2924" />
            </button>)}
        </div>
      </InfoCard>
      {openCollo && <ColloDetailModal collo={openCollo} onClose={() => setOpenColloId(null)} data-id="element-2925" />}
    </>;
}
function ColloDetailModal({
  collo,
  onClose
}: {
  collo: Collo;
  onClose: () => void;
}) {
  const totalQty = collo.items.reduce((s, i) => s + i.qty, 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2926">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-2927" />
      <div className="relative w-full max-w-lg bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2928">
        <div className="p-5 border-b border-[#262626] flex items-start justify-between gap-4 shrink-0" data-id="element-2929">
          <div className="min-w-0" data-id="element-2930">
            <div className="flex items-center gap-2 mb-1" data-id="element-2931">
              <Package className="h-4 w-4 text-[#D4FF3A]" data-id="element-2932" />
              <h2 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-2933">
                {collo.label}
              </h2>
            </div>
            <div className="text-[11px] font-mono text-[#6B6B6B]" data-id="element-2934">
              {collo.dimensions}
              {collo.weight !== '—' && <> · {collo.weight}</>}
              <> · {totalQty} articoli</>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-md flex items-center justify-center text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" aria-label="Chiudi" data-id="element-2935">
            <X className="h-4 w-4" data-id="element-2936" />
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-2" data-id="element-2937">
          {collo.items.map((item, idx) => <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[#181818] border border-[#1F1F1F]" data-id="element-2938">
              <div className="h-9 w-9 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-2939">
                <span className="text-xs font-mono text-[#D4FF3A]" data-id="element-2940">
                  ×{item.qty}
                </span>
              </div>
              <div className="flex-1 min-w-0" data-id="element-2941">
                <div className="text-sm text-[#FAFAFA] font-medium truncate" data-id="element-2942">
                  {item.name}
                </div>
                <div className="text-[10px] font-mono text-[#6B6B6B] flex items-center gap-2" data-id="element-2943">
                  <span data-id="element-2944">SKU {item.sku}</span>
                  {item.size && <>
                      <span data-id="element-2945">·</span>
                      <span data-id="element-2946">Taglia {item.size}</span>
                    </>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}